import React from "react";
import styled, { DefaultTheme } from "styled-components";
import { FiCalendar } from "react-icons/fi";

interface TermSelectionProps {
  onSelect: (term: number) => void;
  currentGrade?: string; // Optional prop to display current grade
  currentSubject?: string; // Optional prop to display current subject
}

const Container = styled.div`
  max-width: 800px;
  margin: 30px auto;
  padding: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-align: center;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 16px;
  font-size: 1.8rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto 20px;
`;

const MetaContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  background-color: ${({ theme }) => theme.primaryLight || "#f3e8ff"};
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TermsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
`;

const TermButton = styled.button`
  background-color: white;
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  border: 2px solid ${({ theme }) => theme.primary || "#7e22ce"};
  border-radius: 10px;
  padding: 16px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 170px;
  justify-content: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.primaryLight || "#f3e8ff"};
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primaryLight || "#f3e8ff"};
  }
`;

const CalendarIcon = styled(FiCalendar)`
  font-size: 1.2rem;
`;

const IllustrationContainer = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
`;

const CalendarIllustration = styled.div`
  width: 80px;
  height: 80px;
  background-color: ${({ theme }) => theme.primaryLight || "#f3e8ff"};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 40px;
    height: 40px;
    color: ${({ theme }) => theme.primary || "#7e22ce"};
  }
`;

const TermSelection: React.FC<TermSelectionProps> = ({ onSelect, currentGrade, currentSubject }) => {
  return (
    <Container>
      <Header>
        <CalendarIcon size={32} />
        <Title>Select Academic Term</Title>
        <Subtitle>Choose which term you want to explore for your curriculum</Subtitle>
        {/* Optional metadata */}
        {(currentGrade || currentSubject) && (
          <div>
            {currentGrade && <span>Grade: {currentGrade} </span>}
            {currentSubject && <span>Subject: {currentSubject}</span>}
          </div>
        )}
      </Header>
      <TermsContainer>
        {[1, 2, 3].map((term) => (
          <TermButton key={term} onClick={() => onSelect(term)}>
            Term {term}
          </TermButton>
        ))}
      </TermsContainer>
    </Container>
  );
};

export default TermSelection;